import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Bell, 
  Check, 
  CheckCheck,
  ArrowUpRight,
  AlertCircle,
  Settings,
  MessageSquare,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification type icons and colors
const getNotificationStyle = (type) => {
  switch (type) {
    case 'transaction_detected':
      return { 
        icon: ArrowUpRight, 
        color: 'text-teal-600', 
        bg: 'bg-teal-50', 
        border: 'border-teal-200' 
      };
    case 'transaction_confirmed':
      return { 
        icon: Check, 
        color: 'text-green-600', 
        bg: 'bg-green-50', 
        border: 'border-green-200' 
      };
    case 'transaction_failed':
      return { 
        icon: X, 
        color: 'text-red-600', 
        bg: 'bg-red-50', 
        border: 'border-red-200' 
      };
    case 'sms_received':
      return { 
        icon: MessageSquare, 
        color: 'text-blue-600', 
        bg: 'bg-blue-50', 
        border: 'border-blue-200' 
      };
    case 'account_update':
      return { 
        icon: Settings, 
        color: 'text-purple-600', 
        bg: 'bg-purple-50', 
        border: 'border-purple-200' 
      };
    default:
      return { 
        icon: Bell, 
        color: 'text-slate-600', 
        bg: 'bg-slate-50', 
        border: 'border-slate-200' 
      };
  }
};

export const Notifications = () => {
  const { merchant } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/merchant/notifications`, { 
        params: { unread_only: filter === 'unread' },
        withCredentials: true 
      });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/merchant/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/merchant/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.is_read)
    : notifications;

  return (
    <div data-testid="notifications-page" className="animate-fadeIn">
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .notif-item { transition: all 0.2s ease; }
        .notif-item:hover { background: linear-gradient(90deg, transparent 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%); }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A1628]">Notifications</h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Filter Buttons */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                filter === 'all' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                filter === 'unread' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Unread
              {unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
          
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              className="transition-all duration-200 hover:scale-105"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
            <Bell className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </h3>
          <p className="text-slate-500">
            {filter === 'unread' 
              ? "You're all caught up!" 
              : "Notifications will appear here when you receive payments"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="divide-y divide-slate-100">
            {filteredNotifications.map((notification, idx) => {
              const style = getNotificationStyle(notification.type);
              const IconComponent = style.icon;
              
              return (
                <div 
                  key={notification.notification_id}
                  className={`p-4 notif-item cursor-pointer ${!notification.is_read ? 'bg-teal-50/30' : ''}`}
                  style={{ animationDelay: `${idx * 30}ms` }}
                  onClick={() => !notification.is_read && markAsRead(notification.notification_id)}
                  data-testid={`notif-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg} ${style.border} border`}>
                      <IconComponent className={`w-5 h-5 ${style.color}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-medium ${notification.is_read ? 'text-slate-600' : 'text-slate-900'}`}>
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                        )}
                      </div>
                      <p className={`text-sm ${notification.is_read ? 'text-slate-400' : 'text-slate-600'}`}>
                        {notification.message}
                      </p>
                      
                      {/* Transaction details if available */}
                      {notification.data?.amount && (
                        <div className="mt-2 flex items-center gap-3 text-xs">
                          <span className="px-2 py-1 bg-slate-100 rounded text-slate-600">
                            {notification.data.currency || 'LRD'} {notification.data.amount?.toLocaleString()}
                          </span>
                          {notification.data.network && (
                            <span className={`px-2 py-1 rounded ${
                              notification.data.network === 'MTN' 
                                ? 'bg-yellow-100 text-yellow-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {notification.data.network}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Time */}
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs text-slate-400">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
